import EventEmitter from 'eventemitter3';
import {
  ConnectionEvent,
  ConnectionState,
  loggerManager,
  Logger,
  SDP_SEMANTICS_UNIFIED_PLAN,
  SDP_SEMANTICS_PLAN_B,
  CONNECTION_CLOSED_REASON
} from 'trtc-js-sdk-core';

import innerEmitter from 'trtc-js-sdk-core/src/inner-emitter/inner-emitter';
import INNER_EVENT from 'trtc-js-sdk-core/src/inner-emitter/inner-event';
import { Room } from '../../room';
import SignalChannel from '../../signal/signal-channel';
import { IConnection } from '../../type/type';
import { DownlinkConnectionOption, UplinkConnectionOption } from '../type';

/**
 * Connection represents a peer connection between the local and remote
 * It handles the events received from RTCPeerConnection and events
 * received from SignalChannel which is dedicated to the connection.
 *
 * For uplink connection, it's also responsible for publishing local stream.
 * For downlink connection, it's responsible for constructing remote stream.
 * There could be two remote streams in one downlink connection, one of the two
 * is the remote stream containing the audio track and the main video track,
 * another one is the remote stream containing only the auxiliary video stream,
 * e.g, the screen sharing stream
 *
 * @export
 * @class Connection
 */
export default abstract class ConnectionSPC extends EventEmitter implements IConnection {
  userId: string;
  tinyId: string;
  protected _sdpSemantics: typeof SDP_SEMANTICS_UNIFIED_PLAN | typeof SDP_SEMANTICS_PLAN_B;
  private _isUplink: boolean;
  protected _room: Room;
  protected _log: Logger;
  protected _currentState: ConnectionState = ConnectionState.DISCONNECTED;
  // 用于记录连接耗时
  protected _prevTime = -1;
  protected _blackSmallVideoDetectionId?: string;
  isDestroyed = false;
  get _peerConnection() {
    return this.singlePC?.getPeerConnection() || null;
  }

  get singlePC() {
    return this._room.singlePC;
  }

  constructor(options: UplinkConnectionOption & { isUplink: true } | DownlinkConnectionOption & { isUplink: false }) {
    super();
    /*
     * the userId who initiates the connection, for uplink connection it's the local userId
     * for downlink connection, it's the remote userId.
     */
    this.userId = options.userId;
    // tinyId is the userId representation in remote server.
    this.tinyId = options.tinyId;
    /** @type {Client} */
    this._room = options.room;
    this._sdpSemantics = options.room.sdpSemantics;
    this._isUplink = options.isUplink;
    this._log = loggerManager.createLogger({
      parent: this._room.getLogger(),
      id: 'n',
      userId: this._room.userId,
      remoteUserId: this._isUplink ? undefined : this.userId,
      sdkAppId: this._room.sdkAppId,
      isLocal: this._isUplink
    });
  }

  get _signalChannel(): SignalChannel {
    // Connection 在创建 SignalChannel 后才会初始化，因此肯定会有 signalChannel
    return this._room.signalChannel!;
  }


  /**
   * Close the connection and release all of its resources
   * For downlink connection, it also cleanup the remote stream
   *
   * @memberof Connection
   */
  close(reason?: string) {
    this._log.info('close connection');
    this.emit(ConnectionEvent.CLOSED, reason);
  }

  destroy() {
    this.isDestroyed = true;
  }

  // 该事件流向 Connection -> Local/Remote VideoTrack -> Stream
  emitConnectionStateChangedEvent(state: ConnectionState) {
    // 状态未变更，不抛事件
    if (state === this._currentState) {
      return false;
    }

    innerEmitter.emit(INNER_EVENT.PEER_CONNECTION_STATE_CHANGED, { room: this._room, prevState: this._currentState, state, remoteUserId: this._isUplink ? undefined : this.userId });
    this.emit(ConnectionEvent.CONNECTION_STATE_CHANGED, {
      prevState: this._currentState,
      state
    });
    this._currentState = state;
    return true;
  }

  /**
   *
   * @package
   * @returns {RTCPeerConnection}
   * @memberof Connection
   */
  getPeerConnection() {
    return this._peerConnection;
  }

  getRoom() {
    return this._room;
  }

  /**
   *
   * @package
   * @returns
   * @memberof Connection
   */
  getUserId() {
    return this.userId;
  }

  /**
   *
   * @package
   */
  getTinyId() {
    return this.tinyId;
  }

  getCurrentState() {
    return this._currentState;
  }

  get isH264() {
    return !!this._peerConnection?.remoteDescription?.sdp.includes('H264');
  }
}
